// Обработчики холста: клики по виджетам, перетаскивание и ресайз сетки, медиазапросы.

import type { GridElementLike } from '../canvas';
import { applyLayoutCommand, desktopFixedCanvasMedia, scheduleCanvasSettle, narrowWorkspaceMedia, phoneWorkspaceMedia, scheduleGridMetricsUpdate, scheduleVisualResize, setGridInteraction, setLastAppliedGridColumns, setLayoutEditing, syncLayoutsFromGrid, updateAllWidgetSizeBadges, updateWidgetSizeBadge, widgetIdFromGridItem } from '../canvas';
import { clearSelectedWidget, selectedWidget, setBuilderOpen, setPaletteOpen } from '../workspace';
import { closeWidgetMenus, duplicateWidget, isWidgetControlTarget, removeWidget, renderWidget, setWidgetPeriod, stepWidgetPeriod, toggleLayoutPopover, toggleWidgetMenu, widgetIdFromTarget } from '../widgets';
import { controls } from '../controls';
import { editWidget } from '../widget-editor';
import { grid } from '../grid';
import { markDirty, setStatus } from '../status';
import { state } from '../state';

export function bindCanvasEvents(): void {
  controls.canvasScroll.addEventListener('pointerdown', (event) => {
    if (state.viewMode || event.button !== 0) return;
    const target = event.target as Element;
    if (isWidgetControlTarget(target)) return;
    const id = widgetIdFromGridItem(target);
    if (id) void editWidget(id);
  }, { capture: true });

  controls.gridRoot.addEventListener('click', (event) => {
    const target = event.target as Element;
    const layoutCommand = target.closest<HTMLElement>('[data-layout-action]');
    if (layoutCommand) {
      const id = widgetIdFromTarget(layoutCommand);
      if (id) applyLayoutCommand(id, layoutCommand.dataset.layoutAction ?? '');
      return;
    }
    const itemElement = target.closest<HTMLElement>('.grid-stack-item');
    const actionElement = target.closest<HTMLElement>('[data-action]');
    if (!actionElement) {
      const id = itemElement ? widgetIdFromGridItem(itemElement) : null;
      if (id && !state.viewMode) void editWidget(id);
      return;
    }
    const id = widgetIdFromTarget(actionElement);
    if (!id) return;
    const action = actionElement.dataset.action;
    if (action === 'period-prev') {
      void stepWidgetPeriod(id, -1);
      return;
    }
    if (action === 'period-next') {
      void stepWidgetPeriod(id, 1);
      return;
    }
    if (state.viewMode) return;
    if (action === 'widget-menu') {
      toggleWidgetMenu(id, actionElement);
      return;
    }
    closeWidgetMenus();
    if (action === 'layout-menu') toggleLayoutPopover(id, actionElement);
    if (action === 'refresh') void renderWidget(id, true);
    if (action === 'edit') void editWidget(id);
    if (action === 'duplicate') duplicateWidget(id);
    if (action === 'delete' && window.confirm('Удалить этот виджет?')) removeWidget(id);
  });
  controls.canvasScroll.addEventListener('click', (event) => {
    if (state.viewMode) return;
    const target = event.target as Element;
    if (
      target.closest('[data-action]')
      || target.closest('[data-widget-menu]')
      || target.closest('[data-layout-popover]')
    ) {
      return;
    }
    const itemElement = target.closest<HTMLElement>('.grid-stack-item');
    if (itemElement) {
      const id = widgetIdFromGridItem(itemElement);
      if (id) void editWidget(id);
      return;
    }
    if (
      target.closest('.canvas-actions')
      || target.closest('.dashboard-filterbar')
      || target.closest('.empty-state')
    ) {
      return;
    }
    clearSelectedWidget(true);
  });

  controls.gridRoot.addEventListener('change', (event) => {
    const select = (event.target as Element).closest<HTMLSelectElement>('[data-period-select]');
    if (!select) return;
    const id = widgetIdFromTarget(select);
    if (id) void setWidgetPeriod(id, select.value);
  });

  grid.on('change', () => {
    scheduleGridMetricsUpdate();
    updateAllWidgetSizeBadges();
  });

  grid.on('dragstart', (_event, item) => {
    if (!state.layoutEditing) return;
    setGridInteraction(true, 'Перемещение', item as GridElementLike);
  });
  grid.on('drag', (_event, item) => {
    if (!state.layoutEditing) return;
    updateWidgetSizeBadge(item as GridElementLike);
  });
  grid.on('dragstop', (_event, item) => {
    if (state.suppressGridEvents) return;
    syncLayoutsFromGrid();
    setGridInteraction(false, undefined, item as GridElementLike);
    scheduleGridMetricsUpdate();
    markDirty();
    setStatus('Положение виджета изменено', 'success');
  });

  grid.on('resizestart', (_event, item) => {
    if (!state.layoutEditing) return;
    setGridInteraction(true, 'Размер', item as GridElementLike);
  });
  grid.on('resize', (_event, item) => {
    const typedItem = item as GridElementLike;
    updateWidgetSizeBadge(typedItem);
    const node = typedItem.gridstackNode;
    if (node?.id) scheduleVisualResize(String(node.id));
  });
  grid.on('resizestop', (_event, item) => {
    if (state.suppressGridEvents) return;
    syncLayoutsFromGrid();
    const typedItem = item as GridElementLike;
    const node = typedItem.gridstackNode;
    if (node?.id) scheduleVisualResize(String(node.id));
    setGridInteraction(false, undefined, typedItem);
    scheduleGridMetricsUpdate();
    markDirty();
    setStatus('Размер виджета изменён', 'success');
  });

  window.addEventListener('resize', () => {
    scheduleGridMetricsUpdate();
    scheduleCanvasSettle();
  });

  desktopFixedCanvasMedia.addEventListener('change', () => {
    setLastAppliedGridColumns(0);
    scheduleGridMetricsUpdate();
  });

  narrowWorkspaceMedia.addEventListener('change', (event) => {
    setBuilderOpen(Boolean(selectedWidget()) && !event.matches);
    setPaletteOpen(!event.matches);
    setLastAppliedGridColumns(0);
    scheduleGridMetricsUpdate();
  });
  phoneWorkspaceMedia.addEventListener('change', (event) => {
    // На телефоне правка раскладки выключается; при возврате к широкому окну
    // в режиме редактирования её нужно вернуть, иначе виджеты не двигаются
    // до перезагрузки.
    if (event.matches) setLayoutEditing(false);
    else if (!state.viewMode) setLayoutEditing(true);
    scheduleGridMetricsUpdate();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && narrowWorkspaceMedia.matches && (state.builderOpen || state.paletteOpen)) {
      setBuilderOpen(false);
      setPaletteOpen(false);
    }
  });

  window.addEventListener('beforeunload', (event) => {
    if (!state.dirty) return;
    event.preventDefault();
  });
}
